// Trade Details Panel - Expandable right pane for detailed trade information
class TradeDetailsPanel {
    constructor() {
        this.panel = null;
        this.currentTrade = null;
        this.isExpanded = false;
        this.sseService = null;
        
        this.init();
    }

    init() {
        this.createPanel();
        this.setupEventListeners();
    }

    createPanel() {
        // Create the main panel element
        this.panel = document.createElement('div');
        this.panel.className = 'trade-details-panel';
        this.panel.innerHTML = this.getPanelHTML();
        
        // Append to body
        document.body.appendChild(this.panel);
        // console.log('Panel created and appended to body:', this.panel);
        
        // Get references to key elements
        this.closeBtn = this.panel.querySelector('.trade-details-close');
        this.content = this.panel.querySelector('.trade-details-content');
        this.title = this.panel.querySelector('.trade-details-title');
        this.subtitle = this.panel.querySelector('.trade-details-subtitle');
    }

    getPanelHTML() {
        return `
            <div class="trade-details-header">
                <h3 class="trade-details-title">Trade Details</h3>
                <p class="trade-details-subtitle">Select a trade to view details</p>
                <button class="trade-details-close">&times;</button>
            </div>
            <div class="trade-details-content">
                <div class="trade-details-loading">
                    <p>Select a trade from the table to view detailed information</p>
                </div>
            </div>
        `;
    }

    setupEventListeners() {
        // Close button
        this.closeBtn.addEventListener('click', () => {
            this.collapse();
        });

        // Close on escape key
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.isExpanded) {
                this.collapse();
            }
        });

        // Close on outside click
        document.addEventListener('click', (e) => {
            if (this.isExpanded && !this.panel.contains(e.target) && !e.target.closest('.table-row, .incoming-trade-row')) {
                this.collapse();
            }
        });
    }

    async showTradeDetails(trade) {
        if (this.currentTrade && this.currentTrade.id === trade.id && this.isExpanded) {
            // Same trade clicked - collapse
            this.collapse();
            return;
        }

        // Store current request ID to handle race conditions
        const requestId = Date.now();
        this.currentRequestId = requestId;

        this.currentTrade = trade;
        
        // Check if we need to fetch full enhanced data
        const needsEnhancedData = !trade.enhanced_data?.clusters?.long_clusters && 
                                  !trade.enhanced_data?.clusters?.short_clusters;
        
        if (needsEnhancedData && trade.id) {
            try {
                // Show loading state immediately
                this.showLoadingState(trade);
                this.expand();
                
                // Fetch full enhanced data from server cache (no DB query)
                const serverUrl = window.CONFIG?.SERVER?.BASE_URL || 'https://atv4.ngrok.app';
                const response = await fetch(`${serverUrl}/trade/${trade.id}/enhanced`);
                
                // Check if this request is still current (user didn't click another trade)
                if (this.currentRequestId !== requestId) {
                    console.log('Request cancelled - user clicked another trade');
                    return;
                }
                
                if (!response.ok) {
                    throw new Error(`Server returned ${response.status}`);
                }
                
                const result = await response.json();
                
                if (!result.success) {
                    throw new Error(result.error || 'Failed to load enhanced data');
                }
                
                // Merge full enhanced_data into trade object
                trade.enhanced_data = {
                    ...trade.enhanced_data,
                    ...result.data
                };
                
                // Check again if request is still current before updating UI
                if (this.currentRequestId !== requestId) {
                    return;
                }
                
                console.log('Enhanced data loaded successfully for trade', trade.id);
                
            } catch (error) {
                console.error('Failed to load enhanced data:', error);
                
                // Only show error if this request is still current
                if (this.currentRequestId === requestId) {
                    this.showErrorState(trade, error.message);
                    return;
                }
            }
        }
        
        // Update content (either with existing data or newly fetched data)
        if (this.currentRequestId === requestId) {
            this.updateContent(trade);
            if (!needsEnhancedData) {
                this.expand();
            }
        }
    }

    showLoadingState(trade) {
        this.title.textContent = `${trade.asset} ${trade.direction.toUpperCase()} Trade`;
        this.subtitle.textContent = 'Loading detailed information...';
        this.content.innerHTML = `
            <div class="trade-details-loading">
                <div class="loading-spinner"></div>
                <p>Loading enhanced trade data...</p>
            </div>
        `;
        this.panel.classList.add('loading');
    }

    showErrorState(trade, errorMessage) {
        this.title.textContent = `${trade.asset} ${trade.direction.toUpperCase()} Trade`;
        this.subtitle.textContent = 'Failed to load details';
        this.content.innerHTML = `
            <div class="trade-details-error">
                <p class="error-message">⚠️ Failed to load enhanced trade data</p>
                <p class="error-detail">${errorMessage}</p>
                <button class="btn btn-primary" onclick="window.tradeDetailsPanel.showTradeDetails(window.tradeDetailsPanel.currentTrade)">Retry</button>
            </div>
        `;
        this.panel.classList.remove('loading');
        this.panel.classList.add('error');
        this.expand();
    }

    async updateContent(trade) {
        console.log('Trade data received in panel:', {
            risk_assessment: trade.enhanced_data?.summary?.risk_assessment,
            long_quality_score: trade.enhanced_data?.summary?.long_quality_score,
            short_quality_score: trade.enhanced_data?.summary?.short_quality_score,
            trend_direction: trade.enhanced_data?.summary?.market_context?.trend?.direction,
            dominant_bias: trade.enhanced_data?.summary?.dominant_bias,
            atr_percent: trade.enhanced_data?.summary?.market_context?.volatility?.atr_percent
        });
        
        this.title.textContent = `${trade.asset} ${trade.direction.toUpperCase()} Trade`;
        this.subtitle.textContent = `Entry: $${parseFloat(trade.entry_price).toFixed(4)} | Target: $${parseFloat(trade.target_price).toFixed(4)}`;
        
        this.content.innerHTML = this.getTradeDetailsHTML(trade);
        await this.loadSections(trade);
    }

    getTradeDetailsHTML(trade) {
        // Build market analysis rows - only show if data exists
        const marketRows = [];
        const trendDirection = trade.enhanced_data?.summary?.dominant_bias || trade.enhanced_data?.summary?.market_context?.trend?.direction;
        const dominantBias = trade.enhanced_data?.summary?.dominant_bias;
        const atrPercent = trade.enhanced_data?.summary?.market_context?.volatility?.atr_percent;
        const riskAssessment = trade.enhanced_data?.summary?.risk_assessment;
        
        if (trendDirection) {
            marketRows.push(`
                <div class="detail-row">
                    <span class="label">Trend Direction:</span>
                    <span class="value">${trendDirection}</span>
                </div>
            `);
        }
        
        if (dominantBias) {
            marketRows.push(`
                <div class="detail-row">
                    <span class="label label-with-help">
                        Dominant Bias:
                        <span class="help-icon" data-tooltip="Overall market direction based on technical indicators and trend analysis. Indicates whether bulls or bears are in control.">?</span>
                    </span>
                    <span class="value">${dominantBias}</span>
                </div>
            `);
        }
        
        if (atrPercent) {
            marketRows.push(`
                <div class="detail-row">
                    <span class="label label-with-help">
                        ATR %:
                        <span class="help-icon" data-tooltip="Average True Range percentage - measures market volatility. Higher values indicate more volatile price movements.">?</span>
                    </span>
                    <span class="value">${(atrPercent * 100).toFixed(2)}%</span>
                </div>
            `);
        }
        
        if (riskAssessment) {
            marketRows.push(`
                <div class="detail-row">
                    <span class="label label-with-help">
                        Risk Assessment:
                        <span class="help-icon" data-tooltip="Overall trade risk evaluation based on market conditions, volatility, and position sizing. Lower risk is preferred.">?</span>
                    </span>
                    <span class="value">${riskAssessment}</span>
                </div>
            `);
        }
        
        // Build support/resistance section
        const supportLevels = trade.enhanced_data?.summary?.market_context?.support_resistance?.support_levels || trade.support_levels;
        const resistanceLevels = trade.enhanced_data?.summary?.market_context?.support_resistance?.resistance_levels || trade.resistance_levels;
        
        const srRows = [];
        const formattedSupport = this.formatLevels(supportLevels, 'support');
        const formattedResistance = this.formatLevels(resistanceLevels, 'resistance');
        
        if (formattedSupport !== 'N/A') {
            srRows.push(`
                <div class="detail-row">
                    <span class="label">Support Levels:</span>
                    <div class="value">${formattedSupport}</div>
                </div>
            `);
        }
        
        if (formattedResistance !== 'N/A') {
            srRows.push(`
                <div class="detail-row">
                    <span class="label">Resistance Levels:</span>
                    <div class="value">${formattedResistance}</div>
                </div>
            `);
        }
        
        return `
            <div class="trade-sections-container">
                ${marketRows.length > 0 ? `
                    <div class="detail-section">
                        <h4>Market Analysis</h4>
                        ${marketRows.join('')}
                    </div>
                ` : ''}

                ${srRows.length > 0 ? `
                    <div class="detail-section">
                        <h4>Support & Resistance</h4>
                        ${srRows.join('')}
                    </div>
                ` : ''}

                <div id="cluster-heatmap-placeholder"></div>
                <div id="price-info-placeholder"></div>
                <div id="trade-metrics-placeholder"></div>
                <div id="cascade-probabilities-placeholder"></div>
                <div id="enhanced-clusters-placeholder"></div>
                <div id="positions-placeholder"></div>
                <div id="additional-data-placeholder"></div>
            </div>
        `;
    }

    async loadSections(trade) {
        try {
            // Load all section modules (added cluster heatmap)
            const [
                { ClusterHeatmapSection },
                { PriceInfoSection },
                { TradeMetricsSection },
                { CascadeProbabilitiesSection },
                { EnhancedClustersSection },
                { PositionsSection },
                { AdditionalDataSection }
            ] = await Promise.all([
                import('./sections/cluster-heatmap-section.js'),
                import('./sections/price-info-section.js'),
                import('./sections/trade-metrics-section.js'),
                import('./sections/cascade-probabilities-section.js'),
                import('./sections/enhanced-clusters-section.js'),
                import('./sections/positions-section.js'),
                import('./sections/additional-data-section.js')
            ]);

            // Initialize sections (added cluster heatmap)
            this.clusterHeatmapSection = new ClusterHeatmapSection();
            this.priceInfoSection = new PriceInfoSection();
            this.tradeMetricsSection = new TradeMetricsSection();
            this.cascadeProbabilitiesSection = new CascadeProbabilitiesSection();
            this.enhancedClustersSection = new EnhancedClustersSection();
            this.positionsSection = new PositionsSection();
            this.additionalDataSection = new AdditionalDataSection();

            // Render sections into placeholders (heatmap at top)
            const heatmapContainer = document.getElementById('cluster-heatmap-placeholder');
            heatmapContainer.innerHTML = this.clusterHeatmapSection.render(trade);
            this.clusterHeatmapSection.init(heatmapContainer);
            
            const priceInfoContainer = document.getElementById('price-info-placeholder');
            priceInfoContainer.innerHTML = this.priceInfoSection.render(trade);
            this.priceInfoSection.init(priceInfoContainer);
            
            const tradeMetricsContainer = document.getElementById('trade-metrics-placeholder');
            tradeMetricsContainer.innerHTML = this.tradeMetricsSection.render(trade);
            this.tradeMetricsSection.init(tradeMetricsContainer);
            
            document.getElementById('cascade-probabilities-placeholder').innerHTML = this.cascadeProbabilitiesSection.render(trade);
            document.getElementById('enhanced-clusters-placeholder').innerHTML = this.enhancedClustersSection.render(trade);
            document.getElementById('positions-placeholder').innerHTML = this.positionsSection.render(trade);
            document.getElementById('additional-data-placeholder').innerHTML = this.additionalDataSection.render(trade);

            // Setup event listeners for remaining sections
            this.cascadeProbabilitiesSection.setupEventListeners();
            this.enhancedClustersSection.setupEventListeners();
            this.positionsSection.setupEventListeners();
            this.additionalDataSection.setupEventListeners();

        } catch (error) {
            console.error('Error loading trade detail sections:', error);
            this.content.innerHTML = '<p class="error-message">Error loading trade details</p>';
        }
    }

    /**
     * Get quality score for the trade direction
     * @param {Object} trade - Trade object
     * @returns {string} - Formatted quality score
     */
    getQualityScore(trade) {
        const qualityScore = trade.direction === 'long' 
            ? trade.enhanced_data?.summary?.long_quality_score 
            : trade.enhanced_data?.summary?.short_quality_score;
        return qualityScore ? (qualityScore * 100).toFixed(1) + '%' : 'N/A';
    }

    /**
     * Format support/resistance levels with badges
     * @param {string|Array} levels - Levels data
     * @param {string} type - 'support' or 'resistance'
     * @returns {string} - Formatted HTML
     */
    formatLevels(levels, type) {
        const parsedLevels = this.parseLevels(levels);
        if (parsedLevels.length === 0) return 'N/A';
        
        return parsedLevels.map(level => 
            `<span class="level-badge ${type}">${this.formatCurrency(level, 4)}</span>`
        ).join('');
    }

    /**
     * Parse support/resistance levels from JSON string or array
     * @param {string|Array} levels - Levels data
     * @returns {Array} - Parsed levels array
     */
    parseLevels(levels) {
        try {
            if (typeof levels === 'string') {
                return JSON.parse(levels);
            } else if (Array.isArray(levels)) {
                return levels;
            }
        } catch (error) {
            console.warn('Failed to parse levels:', error);
        }
        return [];
    }

    /**
     * Format currency value
     * @param {number} value - Currency value
     * @param {number} decimals - Number of decimal places
     * @returns {string} - Formatted currency
     */
    formatCurrency(value, decimals = 4) {
        return parseFloat(value).toFixed(decimals);
    }

    /**
     * Format signal type for display
     * @param {string} signalType - Raw signal type from database
     * @returns {string} - Formatted display name
     */
    formatSignalType(signalType) {
        if (!signalType) return 'N/A';
        
        switch (signalType) {
            case 'ta_based':
                return 'Enhanced';
            case 'ta_range':
                return 'Ranging';
            case 'standard':
                return 'Pure Liquidity';
            default:
                return signalType;
        }
    }

    getPriceInfoHTML(trade) {
        const currentPrice = parseFloat(trade.current_price);
        const entryPrice = parseFloat(trade.entry_price);
        const targetPrice = parseFloat(trade.target_price);
        const stopPrice = parseFloat(trade.stop_price);
        
        const pnlPercent = ((currentPrice - entryPrice) / entryPrice * 100).toFixed(2);
        const pnlClass = parseFloat(pnlPercent) >= 0 ? 'positive' : 'negative';
        
        return `
            <div class="price-info-item">
                <div class="price-info-label">Current Price</div>
                <div class="price-info-value">$${currentPrice.toFixed(4)}</div>
            </div>
            <div class="price-info-item">
                <div class="price-info-label">Entry Price</div>
                <div class="price-info-value">$${entryPrice.toFixed(4)}</div>
            </div>
            <div class="price-info-item">
                <div class="price-info-label">Target Price</div>
                <div class="price-info-value">$${targetPrice.toFixed(4)}</div>
            </div>
            <div class="price-info-item">
                <div class="price-info-label">Stop Loss</div>
                <div class="price-info-value">$${stopPrice.toFixed(4)}</div>
            </div>
            <div class="price-info-item">
                <div class="price-info-label">Unrealized P&L</div>
                <div class="price-info-value ${pnlClass}">${pnlPercent}%</div>
            </div>
            <div class="price-info-item">
                <div class="price-info-label">Risk/Reward</div>
                <div class="price-info-value">${(() => {
                    const entry = parseFloat(trade.entry_price);
                    const tp = parseFloat(trade.target_price);
                    const sl = parseFloat(trade.stop_price);
                    if (entry && tp && sl && !isNaN(entry) && !isNaN(tp) && !isNaN(sl)) {
                        const reward = Math.abs(tp - entry);
                        const risk = Math.abs(entry - sl);
                        return risk > 0 ? (reward / risk).toFixed(2) : '-';
                    }
                    return '-';
                })()}</div>
            </div>
        `;
    }

    getTradeMetricsHTML(trade) {
        // Build metrics items - only show if value exists
        const metricsItems = [];
        
        // Always show confidence and signal type
        metricsItems.push(`
            <div class="enhanced-data-item">
                <span class="enhanced-data-label">Confidence</span>
                <span class="enhanced-data-value">${(parseFloat(trade.confidence) * 100).toFixed(1)}%</span>
            </div>
        `);
        
        metricsItems.push(`
            <div class="enhanced-data-item">
                <span class="enhanced-data-label">Signal Type</span>
                <span class="enhanced-data-value">${this.formatSignalType(trade.signal_type)}</span>
            </div>
        `);
        
        // Only show duration fields if they exist
        if (trade.tp_hours) {
            metricsItems.push(`
                <div class="enhanced-data-item">
                    <span class="enhanced-data-label">TP Hours</span>
                    <span class="enhanced-data-value">${trade.tp_hours}</span>
                </div>
            `);
        }
        
        if (trade.sl_hours) {
            metricsItems.push(`
                <div class="enhanced-data-item">
                    <span class="enhanced-data-label">SL Hours</span>
                    <span class="enhanced-data-value">${trade.sl_hours}</span>
                </div>
            `);
        }
        
        if (trade.duration_confidence) {
            metricsItems.push(`
                <div class="enhanced-data-item">
                    <span class="enhanced-data-label">Duration Confidence</span>
                    <span class="enhanced-data-value">${(parseFloat(trade.duration_confidence) * 100).toFixed(1)}%</span>
                </div>
            `);
        }
        
        return metricsItems.join('');
    }

    getClusterAnalysisHTML(trade) {
        if (!trade.clusters || !Array.isArray(trade.clusters)) {
            return '<p class="text-secondary">No cluster data available</p>';
        }

        return `
            <div class="cluster-list">
                ${trade.clusters.map(cluster => this.getClusterItemHTML(cluster)).join('')}
            </div>
        `;
    }

    getClusterItemHTML(cluster) {
        return `
            <div class="cluster-item">
                <div class="cluster-header">
                    <span class="cluster-direction ${cluster.cluster_type || 'neutral'}">${cluster.cluster_type || 'Unknown'}</span>
                    <span class="cluster-metric-value">$${parseFloat(cluster.center_price || 0).toFixed(4)}</span>
                </div>
                <div class="cluster-metrics">
                    <div class="cluster-metric">
                        <span class="cluster-metric-label">Total Size:</span>
                        <span class="cluster-metric-value">${parseFloat(cluster.total_size || 0).toLocaleString()}</span>
                    </div>
                    <div class="cluster-metric">
                        <span class="cluster-metric-label">Positions:</span>
                        <span class="cluster-metric-value">${cluster.position_count || 0}</span>
                    </div>
                    <div class="cluster-metric">
                        <span class="cluster-metric-label">Trigger Prob:</span>
                        <span class="cluster-metric-value">${cluster.trigger_probability ? (parseFloat(cluster.trigger_probability) * 100).toFixed(1) + '%' : 'N/A'}</span>
                    </div>
                    <div class="cluster-metric">
                        <span class="cluster-metric-label">Stop Price:</span>
                        <span class="cluster-metric-value">$${parseFloat(cluster.dynamic_stop_price || 0).toFixed(4)}</span>
                    </div>
                </div>
            </div>
        `;
    }

    getEnhancedClustersHTML(trade) {
        if (!trade.enhanced_clusters || !Array.isArray(trade.enhanced_clusters)) {
            return '<p class="text-secondary">No enhanced cluster data available</p>';
        }

        return `
            <div class="cluster-list">
                ${trade.enhanced_clusters.map(cluster => this.getEnhancedClusterItemHTML(cluster)).join('')}
            </div>
        `;
    }

    getEnhancedClusterItemHTML(cluster) {
        return `
            <div class="cluster-item">
                <div class="cluster-header">
                    <span class="cluster-direction ${cluster.direction}">${cluster.direction}</span>
                    <span class="cluster-metric-value">$${parseFloat(cluster.center_price || 0).toFixed(4)}</span>
                </div>
                <div class="cluster-metrics">
                    <div class="cluster-metric">
                        <span class="cluster-metric-label">Total Size:</span>
                        <span class="cluster-metric-value">${parseFloat(cluster.total_size || 0).toLocaleString()}</span>
                    </div>
                    <div class="cluster-metric">
                        <span class="cluster-metric-label">Distance:</span>
                        <span class="cluster-metric-value">${cluster.price_distance_pct ? parseFloat(cluster.price_distance_pct).toFixed(2) + '%' : 'N/A'}</span>
                    </div>
                    <div class="cluster-metric">
                        <span class="cluster-metric-label">Risk Score:</span>
                        <span class="cluster-metric-value">${cluster.composite_risk ? parseFloat(cluster.composite_risk).toFixed(2) : 'N/A'}</span>
                    </div>
                    <div class="cluster-metric">
                        <span class="cluster-metric-label">Positions:</span>
                        <span class="cluster-metric-value">${cluster.position_count || 0}</span>
                    </div>
                </div>
                ${cluster.positions ? this.getPositionsHTML(cluster.positions) : ''}
            </div>
        `;
    }

    getPositionsHTML(positions) {
        if (!Array.isArray(positions) || positions.length === 0) {
            return '';
        }

        return `
            <div class="position-list" style="margin-top: 15px;">
                <h5 style="margin-bottom: 10px; color: var(--text-secondary); font-size: 0.9em;">Individual Positions:</h5>
                ${positions.slice(0, 5).map(position => this.getPositionItemHTML(position)).join('')}
                ${positions.length > 5 ? `<p style="text-align: center; color: var(--text-secondary); font-size: 0.8em; margin-top: 10px;">... and ${positions.length - 5} more positions</p>` : ''}
            </div>
        `;
    }

    getPositionItemHTML(position) {
        return `
            <div class="position-item">
                <div class="position-header">
                    <span class="position-trader">${position.trader ? position.trader.substring(0, 8) + '...' : 'Unknown'}</span>
                    <span class="position-size">${parseFloat(position.size || 0).toLocaleString()}</span>
                </div>
                <div class="position-details">
                    <div class="position-detail">
                        <span class="position-detail-label">Price:</span>
                        <span class="position-detail-value">$${parseFloat(position.price || 0).toFixed(4)}</span>
                    </div>
                    <div class="position-detail">
                        <span class="position-detail-label">USD Size:</span>
                        <span class="position-detail-value">$${parseFloat(position.usd_size || 0).toLocaleString()}</span>
                    </div>
                    <div class="position-detail">
                        <span class="position-detail-label">Source:</span>
                        <span class="position-detail-value">${position.source || 'N/A'}</span>
                    </div>
                </div>
            </div>
        `;
    }

    getAdditionalDataHTML(trade) {
        // Build additional data items - only show if value exists
        const dataItems = [];
        
        // Always show Trade ID
        dataItems.push(`
            <div class="enhanced-data-item">
                <span class="enhanced-data-label">Trade ID</span>
                <span class="enhanced-data-value">${trade.id.substring(0, 8)}...</span>
            </div>
        `);
        
        // Show signal timestamp if available
        if (trade.file_timestamp) {
            dataItems.push(`
                <div class="enhanced-data-item">
                    <span class="enhanced-data-label">Signal Timestamp</span>
                    <span class="enhanced-data-value">${new Date(trade.file_timestamp).toLocaleString()}</span>
                </div>
            `);
        }
        
        // Always show database entry
        dataItems.push(`
            <div class="enhanced-data-item">
                <span class="enhanced-data-label">Database Entry</span>
                <span class="enhanced-data-value">${new Date(trade.created_at).toLocaleString()}</span>
            </div>
        `);
        
        // Only show range fields if they exist
        if (trade.tp_range) {
            dataItems.push(`
                <div class="enhanced-data-item">
                    <span class="enhanced-data-label">TP Range</span>
                    <span class="enhanced-data-value">${trade.tp_range}</span>
                </div>
            `);
        }
        
        if (trade.sl_range) {
            dataItems.push(`
                <div class="enhanced-data-item">
                    <span class="enhanced-data-label">SL Range</span>
                    <span class="enhanced-data-value">${trade.sl_range}</span>
                </div>
            `);
        }
        
        return dataItems.join('');
    }

    expand() {
        console.log('Expanding panel...');
        this.panel.classList.add('expanded');
        this.isExpanded = true;
        
        // Debug panel state
        console.log('Panel classes:', this.panel.className);
        console.log('Panel computed style right:', getComputedStyle(this.panel).right);
        console.log('Panel z-index:', getComputedStyle(this.panel).zIndex);
        
        // Adjust main content to make room for panel
        const mainContent = document.querySelector('.main-content-area') || document.querySelector('#main-content');
        if (mainContent) {
            mainContent.classList.add('panel-open');
        }
    }

    collapse() {
        this.panel.classList.remove('expanded');
        this.isExpanded = false;
        this.currentTrade = null;
        
        // Reset main content margin
        const mainContent = document.querySelector('.main-content-area') || document.querySelector('#main-content');
        if (mainContent) {
            mainContent.classList.remove('panel-open');
        }
    }

    // Public method to set SSE service for real-time updates
    setSSEService(sseService) {
        this.sseService = sseService;
    }
}

// Export for ES6 modules
export { TradeDetailsPanel };

// Export for use in other modules
window.TradeDetailsPanel = TradeDetailsPanel;
